import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { replaceWithPostLoginRedirect } from "@/lib/auth-redirect";

export const Route = createFileRoute("/auth/callback")({
  component: AuthCallbackPage,
});

function getOAuthError() {
  const url = new URL(window.location.href);
  const hashParams = new URLSearchParams(url.hash.replace(/^#/, ""));
  return (
    hashParams.get("error_description") ??
    hashParams.get("error") ??
    url.searchParams.get("error_description") ??
    url.searchParams.get("error")
  );
}

function wait(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

class LoginTimeoutError extends Error {
  constructor() {
    super("Ainda estou a validar o login");
    this.name = "LoginTimeoutError";
  }
}

function getAuthParam(key: string) {
  const url = new URL(window.location.href);
  const hashParams = new URLSearchParams(url.hash.replace(/^#/, ""));
  return hashParams.get(key) ?? url.searchParams.get(key);
}

function cleanAuthParamsFromUrl() {
  const url = new URL(window.location.href);
  const hashParams = new URLSearchParams(url.hash.replace(/^#/, ""));
  [
    "access_token",
    "refresh_token",
    "expires_in",
    "expires_at",
    "token_type",
    "type",
    "provider_token",
    "provider_refresh_token",
    "code",
    "error",
    "error_code",
    "error_description",
  ].forEach((key) => {
    url.searchParams.delete(key);
    hashParams.delete(key);
  });
  const nextHash = hashParams.toString();
  window.history.replaceState(window.history.state, document.title, `${url.pathname}${url.search}${nextHash ? `#${nextHash}` : ""}`);
}

async function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new LoginTimeoutError()), ms);
    promise
      .then(resolve)
      .catch(reject)
      .finally(() => window.clearTimeout(timer));
  });
}

async function waitForSession(maxMs: number) {
  const deadline = Date.now() + maxMs;

  while (Date.now() < deadline) {
    const { data } = await withTimeout(supabase.auth.getSession(), 6000).catch(() => ({ data: { session: null } }));
    if (data.session) return data.session;
    await wait(500);
  }

  return null;
}

function AuthCallbackPage() {
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function finishLogin() {
      const oauthError = getOAuthError();
      if (oauthError) {
        setFailed(oauthError);
        return;
      }

      try {
        const accessToken = getAuthParam("access_token");
        const refreshToken = getAuthParam("refresh_token");
        const code = getAuthParam("code");
        let sessionFound = false;

        if (accessToken || refreshToken || code) {
          // Remove one-time OAuth params before setting the session. Setting the
          // session fires auth listeners that can re-render this route; leaving
          // the same code in the URL can trigger a second exchange and fail.
          cleanAuthParamsFromUrl();
        }

        if (accessToken && refreshToken) {
          try {
            const { data, error } = await withTimeout(
              supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken }),
              30000,
            );
            if (error) throw error;
            sessionFound = Boolean(data.session);
          } catch (error) {
            if (!(error instanceof LoginTimeoutError)) throw error;
          }
        }

        if (!sessionFound && code) {
          try {
            const { data, error } = await withTimeout(supabase.auth.exchangeCodeForSession(code), 30000);
            if (error) throw error;
            sessionFound = Boolean(data.session);
          } catch (error) {
            if (!(error instanceof LoginTimeoutError)) throw error;
          }
        }

        if (!sessionFound) {
          const { data } = await withTimeout(supabase.auth.getSession(), 6000);
          sessionFound = Boolean(data.session);
        }

        if (sessionFound) {
          void supabase.auth.getUser().catch(() => undefined);
          replaceWithPostLoginRedirect("/");
          return;
        }
      } catch (error: any) {
        if (!cancelled) setFailed(error?.message || "Não consegui validar o login. Tenta novamente.");
        return;
      }

      const delayedSession = await waitForSession(45000);
      if (cancelled) return;
      if (delayedSession) {
        replaceWithPostLoginRedirect("/");
        return;
      }

      if (!cancelled) setFailed("Não consegui terminar o login. Tenta novamente.");
    }
    finishLogin();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm text-center">
        {failed ? (
          <>
            <h1 className="text-xl font-semibold tracking-tight text-foreground">Login não concluído</h1>
            <p className="mt-2 text-sm text-muted-foreground">{failed}</p>
            <Button asChild className="mt-6 w-full">
              <Link to="/login">Voltar a entrar</Link>
            </Button>
          </>
        ) : (
          <>
            <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" />
            <h1 className="mt-4 text-xl font-semibold tracking-tight text-foreground">A iniciar sessão…</h1>
            <p className="mt-2 text-sm text-muted-foreground">Só demora uns segundos.</p>
          </>
        )}
      </div>
    </main>
  );
}